import { workshopService } from '../services/workshopService';
import React, { useCallback, useContext, useEffect } from 'react';
import { FaSyncAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import ModalForm from '../components/ModalForm';
import { getUserId } from '../config/constants';
import { notifyError, notifySuccess, notifyWarning } from '../utils/notifications';
import { UserContext } from '../utils/UserContext';

const Workshops = () => {
    const { workshops, setWorkshops, setSelectedWorkshop } = useContext(UserContext);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [deleteModalConfig, setDeleteModalConfig] = React.useState({
        isOpen: false,
        workshop: null,
    });
    const navigate = useNavigate();

    const fetchWorkshops = useCallback(async () => {
        try {
            const data = await workshopService.getWorkshops();
            setWorkshops(data);
        } catch (error) {
            console.error('Error al cargar talleres', error);
            setWorkshops([]);
        }
    }, [setWorkshops]);

    // Obtener la lista de talleres al cargar la página
    useEffect(() => {
        fetchWorkshops();
    }, [fetchWorkshops]);

    // Función para crear un nuevo taller
    const handleCreateWorkshop = async (formData) => {
        const { newWorkshop, newWorkshopAddress } = formData;

        if (!newWorkshop || !newWorkshop.trim() || !newWorkshopAddress || !newWorkshopAddress.trim()){
            notifyWarning('El nombre y la dirección del taller son obligatorios.');
            return;
        }

        const workshopData = {
            name: newWorkshop.trim(),
            address: newWorkshopAddress.trim()
        };

        try {
            await workshopService.createWorkshop(workshopData);
            await fetchWorkshops();
            setIsModalOpen(false);
            notifySuccess(`Taller "${newWorkshop}" creado exitosamente.`);
        } catch (error) {
            console.error('Error al crear taller:', error);
            notifyError('No se ha podido crear el taller. Intenta nuevamente.');
        }
    };

    // Función para seleccionar un taller
    const handleSelectWorkshop = (workshop) => {
        const userId = getUserId();
        let userRole = '';
        
        if (workshop.owner_id === userId) {
            userRole = 'owner';
        } else if (workshop.admins && workshop.admins.includes(userId)) {
            userRole = 'Administrador';
        } else if (workshop.technicians && workshop.technicians.includes(userId)) {
            userRole = 'Técnico';
        }
        
        localStorage.setItem('rol', userRole);
        setSelectedWorkshop(workshop);
        navigate('/ordenes');
    };


    // Función para eliminar un taller
    const handleDeleteWorkshop = async (workshop) => {
        setDeleteModalConfig({
            isOpen: true,
            workshop,
        });
    };

    const handleCancelDelete = () => {
        setDeleteModalConfig({
            isOpen: false,
            workshop: null,
        });
    };

    const handleConfirmDelete = async () => {
        const workshop = deleteModalConfig.workshop;
        if (!workshop) {
            handleCancelDelete();
            return;
        }

        const workshopId = workshop._id || workshop.id;
        if (!workshopId) {
            notifyError('No se pudo identificar el taller a eliminar.');
            handleCancelDelete();
            return;
        }

        try {
            await workshopService.deleteWorkshop(workshopId);

            const selected = localStorage.getItem('selectedWorkshop');
            if (selected) {
                try {
                    const parsedSelected = JSON.parse(selected);
                    const selectedId = parsedSelected?._id || parsedSelected?.id;
                    if (selectedId === workshopId) {
                        localStorage.removeItem('selectedWorkshop');
                        setSelectedWorkshop(null);
                    }
                } catch (error) {
                    console.error('Error al parsear selectedWorkshop:', error);
                    localStorage.removeItem('selectedWorkshop');
                    setSelectedWorkshop(null);
                }
            }

            notifySuccess(`Taller "${workshop.name}" eliminado exitosamente.`);
            handleCancelDelete();
            await fetchWorkshops();
        } catch (error) {
            console.error('Error al eliminar taller:', error);

            const status = error?.response?.status;
            if (status === 401) {
                notifyError('Tu sesión expiró. Inicia sesión de nuevo para eliminar talleres.');
            } else if (status === 403) {
                notifyError('No tienes permisos para eliminar este taller.');
            } else if (status === 404) {
                notifyError('El endpoint de eliminación no existe en backend.');
            } else {
                notifyError('No se pudo eliminar el taller. Intenta nuevamente.');
            }

            handleCancelDelete();
        }
    };

    return (
        <div className="workshops-container">
            <div className="header">
                <h1>Gestión de Talleres</h1>
            </div>
            {workshops.length === 0 ? (
                <p className="no-workshops">No hay talleres registrados. Crea uno nuevo para continuar.</p>
            ) : (
                <ul className="workshops-list">
                    {workshops.map((ws) => (
                        <li key={ws._id} className="workshop-item" onClick={() => handleSelectWorkshop(ws)}>
                            <span
                                className="workshop-name"
                            >
                                {ws.name}
                            </span>
                            <span
                                className="action-text delete"
                                onClick={() => handleDeleteWorkshop(ws)}
                            >
                                Eliminar
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            <div className="actions-container">
                <button
                    className="refresh-workshops-btn"
                    onClick={fetchWorkshops}
                >
                    <FaSyncAlt />
                </button>
                <button
                    className="create-workshop-btn"
                    onClick={() => setIsModalOpen(true)}
                >
                    Crear nuevo taller
                </button>
            </div>
            <ModalForm
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleCreateWorkshop}
                title="Crear Nuevo Taller"
                fields={[
                    { name: 'newWorkshop', label: 'Nombre del Taller', placeholder: 'Ej. Taller ABC', required: true },
                    { name: 'newWorkshopAddress', label: 'Dirección', placeholder: 'Ej. Calle 123', required: true },
                ]}
            />

            {deleteModalConfig.isOpen && (
                <div className="modal-overlay" onClick={handleCancelDelete}>
                    <div className="modal-container modal-medium" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Confirmar eliminación</h2>
                        </div>
                        <div className="modal-body">
                            <p>
                                ¿Seguro que quieres eliminar el taller "{deleteModalConfig.workshop?.name}"?
                            </p>
                            <p>Esta acción no se puede deshacer.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={handleCancelDelete}>
                                Cancelar
                            </button>
                            <button className="btn-save" onClick={handleConfirmDelete}>
                                Eliminar taller
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Workshops;